import {Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faChevronLeft,
  faCircleInfo,
  faFloppyDisk,
  faPaperPlaneTop,
  faPlay,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {
  BicButtonComponent,
  BicPopupComponent,
  BicToastService,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {PageHeaderComponent} from '../../util/design/page-header/page-header.component';
import {WorkflowService} from '../../../services/workflow.service';
import {StateConfigService} from '../../../services/state/state-config.service';
import {WorkflowDefinition} from '../../../api/model/workflowDefinition';
import {BuilderStepperComponent} from './builder-stepper/builder-stepper.component';
import {BuilderRuleStepComponent} from './builder-rule-step/builder-rule-step.component';
import {BuilderFileStepComponent} from './builder-file-step/builder-file-step.component';
import {BuilderStartStepComponent} from './builder-start-step/builder-start-step.component';
import {BuilderReportStepComponent} from './builder-report-step/builder-report-step.component';
import {PermissionService} from '../../../services/permission/permission.service';
import {AdminBadgeComponent} from '../../util/design/admin-badge/admin-badge.component';
import {ChatService} from '../../chatBIC/service/chat.service';
import {CatalogService} from '../../../services/catalog.service';
import {
  CatalogSubmissionPromptComponent
} from '../shared/catalog-submission-prompt/catalog-submission-prompt.component';
import {serverErrorMessage} from '../shared/server-error-message';
import {CheckRunScheduleView} from '../../../api/model/checkRunScheduleView';
import {CheckRunScheduleApi} from '../../../services/check-run-schedule/check-run-schedule.api';
import {describeRecurrence, formatBerlin} from '../../../services/check-run-schedule/check-run-schedule.format';
import {
  CheckRunSchedulePopupComponent,
} from '../check-run-schedules/check-run-schedule-popup/check-run-schedule-popup.component';
import {
  buildManifestFromState,
  createDefaultBuilderState,
  getInvalidSteps,
  getMissingRequiredFields,
  MissingRequiredField,
  MyCheckBuilderState,
  parseManifestToState,
  validateBuilderState,
} from './my-checks-builder.model';
import {scheduleBlockedBySelection, scheduleBlockedByStatus} from './builder-schedule-eligibility';
import {Manifest} from '../../../api/model/manifest';

@Component({
  selector: 'my-checks-builder',
  standalone: true,
  imports: [
    FaIconComponent,
    BicButtonComponent,
    PageHeaderComponent,
    BuilderStepperComponent,
    BuilderRuleStepComponent,
    BuilderFileStepComponent,
    BuilderStartStepComponent,
    BuilderReportStepComponent,
    AdminBadgeComponent,
    BicPopupComponent,
    TooltipDirective,
    CatalogSubmissionPromptComponent,
    CheckRunSchedulePopupComponent,
  ],
  templateUrl: './my-checks-builder.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./my-checks-builder.component.scss'],
})
export class MyChecksBuilderComponent implements OnInit, OnDestroy {
  faChevronLeft = faChevronLeft;
  faFloppyDisk = faFloppyDisk;
  faPlay = faPlay;
  faSpinnerThird = faSpinnerThird;
  faCircleInfo = faCircleInfo;
  faPaperPlaneTop = faPaperPlaneTop;
  faTrash = faTrash;
  definition: WorkflowDefinition | null = null;
  state: MyCheckBuilderState = createDefaultBuilderState();
  loading = true;
  saving = false;
  private initialState = '';

  /**
   * True, solange ungespeicherte Änderungen vorliegen (steuert Speichern/Zurück-Button, analog zu
   * den myCheck-Editoren). `currentStep`/`expertMode` sind reine Wizard-/UI-Felder des Builder-
   * State (fließen nicht ins gespeicherte Manifest ein) und bleiben deshalb außen vor – sonst würde
   * bloßes Durchklicken der Schritte schon als Änderung zählen.
   */
  get isDirty(): boolean {
    return !!this.initialState && this.initialState !== this.serializePersistedState();
  }
  testing = false;
  validationErrors: string[] = [];
  /** Fehlende Pflichtangaben (für Banner & Inline-Hinweise), nach Speicherversuch befüllt. */
  missingFields: MissingRequiredField[] = [];
  /** Builder-Schritte mit fehlenden Pflichtangaben (für Stepper-Markierung). */
  invalidSteps: number[] = [];
  /** Erst nach dem ersten „Übernehmen" werden Validierungshinweise angezeigt. */
  showValidation = false;
  currentStep = 0;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private workflowService = inject(WorkflowService);
  private stateConfigService = inject(StateConfigService);
  private toastService = inject(BicToastService);
  /**
   * Admin-Modus: die zentral bereitgestellte Kopie wird projektlos bearbeitet (Route-`data`-Flag,
   * wie bei den Regelwerk-Editoren unter `admin/rulesets`).
   */
  protected adminGlobal = false;
  /**
   * Admin-Modus in einer Stage mit Einreichen (Beta): gespeichert wird immer in einen Entwurf der
   * Kopie, nie in die Kopie selbst. Geladen wird der offene Entwurf oder – ohne Entwurf – die
   * installierte Fassung; der Entwurf entsteht erst beim ersten Speichern (`PUT …/draft`).
   */
  protected adminDraft = false;
  /** ID des offenen Entwurfs; `null`, solange keiner existiert (dann ist `definition` die Kopie). */
  protected draftId: string | null = null;
  /**
   * Admin-Modus in einer Stage ohne Einreichen (Live): die installierte Fassung wird nur angezeigt.
   * Kein Speichern, kein Entwurf; das Backend lehnt Schreibzugriffe dort ohnehin mit 403 ab.
   */
  protected adminReadOnly = false;
  /** Bestätigung „Entwurf verwerfen" offen. */
  protected confirmDiscard = false;
  protected discarding = false;
  /** Einreichen-Dialog für den Entwurf offen. */
  protected submissionOpen = false;
  protected chatService = inject(ChatService);
  private stepTitles = ['Start Prüflauf', 'Regelauswahl', 'Dateiauswahl', 'Bericht erstellen'];
  private definitionId: string | null = null;
  private permissionService = inject(PermissionService);
  private catalogService = inject(CatalogService);
  /** Im Admin-Modus: ID der Freigabe, über die geladen und gespeichert wird. */
  private shareId: string | null = null;
  private scheduleApi = inject(CheckRunScheduleApi);

  /** Zeitplan dieses Prüflaufs im aktuellen Projekt (oder null). */
  protected schedule: CheckRunScheduleView | null = null;
  /** Text für die Infozeile im Startschritt, aus {@link schedule} berechnet. */
  protected scheduleSummary: string | null = null;
  /** Grund, warum die Kachel „Zeitplan“ gesperrt ist; null = klickbar. */
  protected scheduleUnavailableReason: string | null = 'Erst nach dem ersten Speichern verfügbar.';
  protected schedulePopupOpen = false;
  /** Zuletzt geladenes bzw. gespeichertes Manifest; Grundlage der Zeitplan-Eignung ohne Änderungen. */
  private savedManifest: Manifest | null = null;

  /**
   * Projekt-ID oder `undefined` im Admin-Modus.
   *
   * <p>Bewusst nicht `?? 0`: eine `0` wird als `"0"` in HTTP-Params serialisiert und läuft dann in
   * Rechteprüfungen, statt den projektlosen Pfad zu nehmen. Die Steps prüfen daher auf `undefined`.
   */
  get projectId(): number | undefined {
    return this.adminGlobal ? undefined : this.stateConfigService.getProjectIdOrUndefined();
  }

  get currentStepTitle(): string {
    return this.stepTitles[this.currentStep] ?? '';
  }

  ngOnInit(): void {
    this.adminGlobal = this.route.snapshot.data['adminGlobal'] === true;
    this.shareId = this.route.snapshot.paramMap.get('shareId');

    // Der Prüflauf-Builder ist reiner Expertenmodus. Der myCheck-Quickstart hängt seit 08/2026 am
    // myCheck-Editor (my-checks/:rulesetId/ki) – ein myCheck ist selbst ausführbar, ein Prüflauf
    // muss dafür nicht konfiguriert werden.
    if (!this.adminGlobal) {
      this.initializeChatServiceForProject();
    }
    this.definitionId = this.route.snapshot.paramMap.get('definitionId');

    if (this.adminGlobal && this.shareId) {
      this.loadAdminDefinition(this.shareId);
      return;
    }

    if (this.definitionId) {
      this.loading = true;
      this.workflowService.getDefinition(this.definitionId).subscribe({
        next: def => {
          // Die Builder-Route hat nur den AuthGuard – ein eingefügter Deep-Link würde einen
          // schreibgeschützten Check im Editor öffnen. Zentral bereitgestellte Checks tragen im
          // Projekt readOnly=true und werden hier abgefangen.
          if (def.readOnly && !this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN')) {
            this.loading = false;
            this.toastService.error(
              'Dieser myCheck ist zentral bereitgestellt und kann nur ausgeführt oder dupliziert werden.');
            void this.router.navigate(
              ['projects', this.stateConfigService.selectedProject?.id, 'check-runs']);
            return;
          }
          this.definition = def;
          const manifest = this.workflowService.parseManifest(def);
          this.savedManifest = manifest;
          const parsed = parseManifestToState(manifest);
          this.state = {...this.state, ...parsed};
          this.state.expertMode = true;
          this.updateChatMode();
          this.loading = false;
          this.markSaved();
          this.loadSchedule();
          this.applyRequestedStep();
        },
        error: () => {
          this.loading = false;
          this.toastService.error('Prüfablauf konnte nicht geladen werden');
        },
      });
    } else {
      this.state.expertMode = true;
      this.updateChatMode();
      this.loading = false;
      this.markSaved();
      this.refreshScheduleAvailability();
    }
  }

  ngOnDestroy(): void {
    this.chatService.closeMycheckSurface();
  }

  onStepChange(step: number): void {
    this.currentStep = step;
    this.state.currentStep = step;
  }

  /**
   * Zurück zur Übersicht.
   *
   * <p>`selectedProject` ist mit `!` deklariert – ohne die Admin-Verzweigung gäbe es hier im
   * projektlosen Modus einen `TypeError`.
   */
  goBack(): void {
    void this.router.navigate(this.overviewRoute());
  }

  navigateToRuleManagement(): void {
    void this.router.navigate(this.adminGlobal
      ? ['admin', 'my-checks']
      : ['projects', this.stateConfigService.selectedProject?.id, 'my-checks']);
  }

  testWorkflow(): void {
    const errors = validateBuilderState(this.state);
    if (errors.length > 0) {
      this.validationErrors = errors;
      return;
    }
    this.validationErrors = [];

    // Save first, then trigger
    if (!this.definition?.id) return;
    // Ausführen ist projektgebunden (Dateien, Kontingent) – im Admin-Modus gibt es kein Projekt.
    // Der Aufruf ist im Template ohnehin auskommentiert; die Sperre hält das explizit fest.
    if (this.adminGlobal) return;
    const projectId = this.projectId;
    if (!projectId) return;
    const manifest = buildManifestFromState(this.state);

    this.testing = true;
    this.workflowService.updateDefinition(this.definition.id, manifest, projectId).subscribe({
      next: () => {
        this.workflowService.triggerWorkflow(this.definition!.id!, undefined, projectId).subscribe({
          next: instance => {
            this.testing = false;
            this.router.navigate([
              'projects',
              this.stateConfigService.selectedProject?.id,
              'check-runs',
              'execute',
            ], {queryParams: {instanceId: instance.id}});
          },
          error: () => {
            this.testing = false;
            this.toastService.error('Prüfablauf konnte nicht gestartet werden');
          },
        });
      },
      error: () => {
        this.testing = false;
        this.toastService.error('Speichern vor Test fehlgeschlagen');
      },
    });
  }

  private markSaved(): void {
    this.initialState = this.serializePersistedState();
  }

  /** Serialisiert den State ohne die Wizard-/UI-Felder `currentStep`/`expertMode` (siehe isDirty). */
  private serializePersistedState(): string {
    const {currentStep, expertMode, ...persisted} = this.state;
    return JSON.stringify(persisted);
  }

  /**
   * „Übernehmen": speichert IMMER – auch unvollständig. Bei fehlenden Pflichtangaben wird der
   * Check als nicht valide gespeichert (isValid=false → Status DRAFT, serverseitig erzwungen) und
   * der Nutzer bleibt zur Korrektur auf der Seite. Nach erfolgreichem Speichern wird nicht mehr
   * automatisch navigiert – der Button wechselt zu „Zurück" (isDirty=false), ein zweiter Klick
   * navigiert dann tatsächlich zur Übersicht (analog zu den myCheck-Editoren).
   */
  saveWorkflow(afterSave?: () => void): void {
    if (!this.definition?.id) return;
    const projectId = this.projectId;
    // Im Admin-Modus ist projectId absichtlich undefined – dort läuft das Speichern über die Freigabe.
    if (!this.adminGlobal && !projectId) return;
    // Im Admin-Modus wird nur der Entwurf gespeichert – die installierte Fassung (Live) nie.
    if (this.adminGlobal && (!this.shareId || !this.adminDraft)) return;

    this.showValidation = true;
    this.refreshValidation();
    const incomplete = this.missingFields.length > 0;

    this.saving = true;
    const manifest = buildManifestFromState(this.state);

    const save$ = this.adminGlobal
      ? this.workflowService.updateSharedDraft(this.shareId!, manifest)
      : this.workflowService.updateDefinition(this.definition.id, manifest, projectId!);

    save$.subscribe({
      next: saved => {
        this.saving = false;
        // Die Antwort ist der (ggf. gerade angelegte) Entwurf – ab jetzt gibt es Verwerfen/Einreichen.
        if (this.adminGlobal) {
          this.definition = saved;
          this.definitionId = saved.id ?? null;
          this.draftId = saved.id ?? null;
        } else {
          // Das Backend leitet den Status beim Speichern neu ab (vollständig → ACTIVE, sonst DRAFT);
          // die Kachel „Zeitplan“ hängt daran.
          this.definition = {...this.definition!, status: saved.status};
        }
        this.savedManifest = manifest;
        this.markSaved();
        this.refreshScheduleAvailability();
        if (incomplete) {
          this.toastService.warning('Als Entwurf gespeichert – es fehlen noch Pflichtangaben');
        } else {
          this.toastService.success(this.adminGlobal
            ? 'Entwurf gespeichert – wirksam erst nach Einreichen und Freigabe im Admin Center'
            : 'Prüfablauf gespeichert');
        }
        afterSave?.();
      },
      error: err => {
        this.saving = false;
        this.toastService.error(serverErrorMessage(err, 'Speichern fehlgeschlagen'));
      },
    });
  }

  /** „Als neue Version einreichen": nur mit gespeichertem Entwurf – eingereicht wird der Serverstand. */
  requestSubmission(): void {
    if (!this.adminDraft || this.isDirty || !this.draftId) return;
    this.submissionOpen = true;
  }

  closeSubmission(): void {
    this.submissionOpen = false;
  }

  requestDiscard(): void {
    if (!this.adminDraft || !this.draftId) return;
    this.confirmDiscard = true;
  }

  cancelDiscard(): void {
    if (this.discarding) return;
    this.confirmDiscard = false;
  }

  /** Verwirft den Entwurf. Die installierte Fassung bleibt unverändert. */
  discardDraft(): void {
    if (!this.adminDraft || !this.draftId || !this.shareId || this.discarding) return;
    this.discarding = true;
    this.workflowService.discardSharedDraft(this.shareId).subscribe({
      next: () => {
        this.discarding = false;
        this.confirmDiscard = false;
        this.toastService.success('Entwurf verworfen');
        this.goBack();
      },
      error: err => {
        this.discarding = false;
        this.toastService.error(serverErrorMessage(err, 'Entwurf konnte nicht verworfen werden'));
      },
    });
  }

  onStateChange(partial: Partial<MyCheckBuilderState>): void {
    this.state = {...this.state, ...partial};
    this.refreshScheduleAvailability();
    // Hinweise erst nach dem ersten Speicherversuch live aktualisieren.
    if (this.showValidation) {
      this.refreshValidation();
    }
  }

  /** Berechnet fehlende Pflichtangaben neu und befüllt Banner-/Stepper-State. */
  private refreshValidation(): void {
    this.missingFields = getMissingRequiredFields(this.state);
    this.validationErrors = this.missingFields.map(m => m.message);
    this.invalidSteps = getInvalidSteps(this.state);
  }

  /**
   * Öffnet das Zeitplan-Popup. Mit ungespeicherten Änderungen wird zuerst gespeichert: die Vorschau
   * im Popup prüft den gespeicherten Stand im Backend und meldete sonst z. B. eine gerade erst
   * entfernte manuelle Auswahl weiter als Hinderungsgrund. Ist der Prüflauf danach nicht planbar
   * (etwa als Entwurf gespeichert), bleibt das Popup zu und die Kachel nennt den Grund.
   */
  openSchedulePopup(): void {
    if (this.scheduleUnavailableReason !== null || !this.definition?.id || !this.projectId || this.saving) return;
    if (this.isDirty) {
      this.saveWorkflow(() => {
        if (this.scheduleUnavailableReason === null) this.schedulePopupOpen = true;
      });
      return;
    }
    this.schedulePopupOpen = true;
  }

  closeSchedulePopup(): void {
    this.schedulePopupOpen = false;
  }

  onScheduleSaved(schedule: CheckRunScheduleView): void {
    this.schedulePopupOpen = false;
    this.applySchedule(schedule);
  }

  onScheduleDeleted(): void {
    this.schedulePopupOpen = false;
    this.applySchedule(null);
  }

  /**
   * Lädt den Zeitplan des gespeicherten Prüflaufs im aktuellen Projekt (nicht im Admin-Modus).
   * Ein Ladefehler blendet nur die Infozeile aus; der Builder bleibt bedienbar.
   */
  private loadSchedule(): void {
    this.refreshScheduleAvailability();
    const projectId = this.projectId;
    const definitionId = this.definition?.id;
    if (this.adminGlobal || !projectId || !definitionId) return;
    this.scheduleApi.list(projectId).subscribe({
      next: schedules => this.applySchedule(schedules.find(s => s.definitionId === definitionId) ?? null),
      error: err => {
        console.error('Zeitplan des Prüflaufs konnte nicht geladen werden', err);
        this.applySchedule(null);
      },
    });
  }

  private applySchedule(schedule: CheckRunScheduleView | null): void {
    this.schedule = schedule;
    if (!schedule || schedule.status === 'FINISHED') {
      this.scheduleSummary = null;
      return;
    }
    const recurrence = describeRecurrence(schedule);
    this.scheduleSummary = schedule.status === 'PAUSED'
      ? `${recurrence} · pausiert`
      : schedule.nextRunAt
        ? `${recurrence} · nächste Ausführung ${formatBerlin(schedule.nextRunAt)}`
        : recurrence;
  }

  /**
   * Ob die Kachel „Zeitplan“ klickbar ist. Der Status muss `ACTIVE` sein
   * ({@link scheduleBlockedByStatus}, immer der gespeicherte). Die Auswahlregel steht in
   * {@link scheduleBlockedBySelection}: ungespeichert zählt der Stand im Builder, sonst der
   * gespeicherte. Die maßgebliche Eignung (gespeicherter Stand) zeigt die Vorschau im Popup.
   */
  private refreshScheduleAvailability(): void {
    let reason: string | null = null;
    const statusReason = this.definition ? scheduleBlockedByStatus(this.definition.status) : null;
    const dirty = this.isDirty;
    if (this.adminGlobal || !this.projectId) {
      reason = 'Zeitpläne werden im Projekt angelegt.';
    } else if (!this.definition?.id) {
      reason = 'Erst nach dem ersten Speichern verfügbar.';
    } else if (statusReason !== null && (!dirty || this.definition.status === 'ARCHIVED')) {
      // Mit Änderungen speichert der Klick zuerst; vollständig gespeichert wird der Prüflauf ACTIVE
      // (WorkflowDefinitionService.update), nur ARCHIVED bleibt.
      reason = statusReason;
    } else if (dirty && getMissingRequiredFields(this.state).length > 0) {
      reason = 'Erst verfügbar, wenn alle Pflichtangaben ausgefüllt sind.';
    } else if (scheduleBlockedBySelection(this.state, this.savedManifest, this.isDirty)) {
      reason = 'Nur bei vordefinierter Regel- und Dateiauswahl möglich.';
    } else if (!this.permissionService.hasProjectPrivilege('PROJECT_CHECK_EXECUTE')) {
      reason = 'Sie haben nicht die erforderlichen Berechtigungen, um Zeitpläne zu verwalten.';
    }
    this.scheduleUnavailableReason = reason;
  }

  /**
   * `?step=` aus „Auswahl im Prüflauf festlegen“ (Zeitplan-Popup der Übersicht): öffnet den Schritt,
   * in dem die beim Start abgefragte Auswahl festgelegt wird.
   */
  private applyRequestedStep(): void {
    const step = Number(this.route.snapshot.queryParamMap.get('step'));
    if (!Number.isInteger(step) || step <= 0 || step >= this.stepTitles.length) return;
    this.onStepChange(step);
    this.toastService.info('Legen Sie die Auswahl fest und speichern Sie. Danach lässt sich im Schritt '
      + '„Start Prüflauf“ ein Zeitplan anlegen.');
  }

  /** Zielroute der Übersicht, je Modus. */
  private overviewRoute(): any[] {
    return this.adminGlobal
      ? ['admin', 'check-runs']
      : ['projects', this.stateConfigService.selectedProject?.id, 'check-runs'];
  }

  /**
   * Admin-Modus: in Beta den offenen Entwurf laden, ohne Entwurf (404) die installierte Fassung.
   * In Live nur die installierte Fassung, schreibgeschützt. Beim Öffnen wird nichts angelegt.
   */
  private loadAdminDefinition(shareId: string): void {
    this.loading = true;
    this.catalogService.getConfig().subscribe({
      next: config => {
        this.adminDraft = config.submissionAllowed;
        this.adminReadOnly = !config.submissionAllowed;
        if (!config.submissionAllowed) {
          this.loadInstalledDefinition(shareId);
          return;
        }
        this.workflowService.getSharedDraft(shareId).subscribe({
          next: draft => this.applyAdminDefinition(draft, draft.id ?? null),
          error: (err: HttpErrorResponse) => {
            // 404 = kein offener Entwurf, der Normalfall. Alles andere ist ein echter Fehler.
            if (err.status === 404) {
              this.loadInstalledDefinition(shareId);
              return;
            }
            this.loading = false;
            this.toastService.error(
              serverErrorMessage(err, 'Der Entwurf des bereitgestellten Prüflaufs konnte nicht geladen werden'));
          },
        });
      },
      error: err => {
        this.loading = false;
        this.toastService.error(
          serverErrorMessage(err, 'Katalog-Konfiguration konnte nicht geladen werden'));
      },
    });
  }

  /** Die installierte Fassung der Kopie (ohne Entwurf bzw. in Live). */
  private loadInstalledDefinition(shareId: string): void {
    this.workflowService.getSharedDefinition(shareId).subscribe({
      next: def => this.applyAdminDefinition(def, null),
      error: err => {
        this.loading = false;
        this.toastService.error(
          serverErrorMessage(err, 'Der bereitgestellte Prüflauf konnte nicht geladen werden'));
      },
    });
  }

  private applyAdminDefinition(def: WorkflowDefinition, draftId: string | null): void {
    this.definition = def;
    this.definitionId = def.id ?? null;
    this.draftId = draftId;
    const manifest = this.workflowService.parseManifest(def);
    this.savedManifest = manifest;
    this.state = {...this.state, ...parseManifestToState(manifest)};
    this.state.expertMode = true;
    this.loading = false;
    this.markSaved();
    this.refreshScheduleAvailability();
  }

  private initializeChatServiceForProject(): void {
    this.chatService.isFloatingMode = false;
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) return;

    if (this.chatService.currentProject && this.chatService.currentProject !== projectId) {
      this.chatService.handleProjectChange(projectId);
      return;
    }

    this.chatService.currentProject = projectId;
    this.chatService.initializeChat();
  }

  /**
   * Hier läuft kein Quickstart – der Chat bleibt im klassischen Modus.
   *
   * <p>Vorher schaltete der Builder in den 'mycheck'-Modus und ließ den Agenten den Prüflauf
   * überschreiben. Das ist entfallen: chatBIC baut myChecks, keine Prüfläufe.
   */
  private updateChatMode(): void {
    this.chatService.closeMycheckSurface();
  }
}
